import { useMemo } from 'react';
import type { ListTableRow } from 'framework/ui/ui-builder';
import { highlightMatches, SearchHighlightProvider } from 'framework/ui/searchHighlight';
import type { FilterBarProps } from '../../components/FilterBar';
import CollectionView, { CollectionViewProps } from './CollectionView';
import useCollectionColumns from './useCollectionColumns/useCollectionColumns';
import { getCollectionEmptyContent } from './collectionEmptyContent';

export interface CollectionContentLayoutProps<Row extends ListTableRow = ListTableRow> extends Omit<
  CollectionViewProps<Row>,
  'columnVisibilityControl'
> {
  filterBarProps?: FilterBarProps;
}

const CollectionContentLayout = <Row extends ListTableRow>({
  filterBarProps,
  listProps,
  columnVisibility,
  highlightQuery = '',
  loading,
  ...contentProps
}: CollectionContentLayoutProps<Row>) => {
  const { columns, columnsProps: columnVisibilityControl } = useCollectionColumns(listProps.columns, columnVisibility);
  const highlightedColumns = useMemo(
    () =>
      columns.map(column => ({
        ...column,
        render: (row: Row) => highlightMatches(column.render(row), highlightQuery),
      })),
    [columns, highlightQuery],
  );

  return (
    <SearchHighlightProvider value={highlightQuery}>
      <CollectionView
        {...contentProps}
        loading={loading}
        columnVisibilityControl={columnVisibilityControl}
        listProps={{
          ...listProps,
          columns: highlightedColumns,
          emptyContent: getCollectionEmptyContent({
            emptyContent: listProps.emptyContent,
            searchValue: highlightQuery,
            filterProps: filterBarProps?.filterProps,
            loading,
          }),
        }}
      />
    </SearchHighlightProvider>
  );
};

export default CollectionContentLayout;
